// A "count + label" stat tile — the building block of dashboard tile
// strips and per-type filter rows. Presentational; wrap it in a
// <button>/<Link> for a clickable filter/nav tile (`interactive` for the
// hover affordance, `active` for the selected ring, `dimmed` for a
// zero-value state).

import { cn } from "../lib/cn";

export type StatTone = "default" | "success" | "warning" | "danger" | "muted";

const TONE_CLASS: Record<StatTone, string> = {
  default: "text-foreground",
  success: "text-success",
  warning: "text-warning",
  danger: "text-destructive",
  muted: "text-muted-foreground",
};

export function StatTile({
  label,
  value,
  sub,
  icon,
  tone = "default",
  active,
  dimmed,
  interactive,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  /** A rendered icon, shown top-right. */
  icon?: React.ReactNode;
  tone?: StatTone;
  active?: boolean;
  dimmed?: boolean;
  interactive?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        // Same elevation language as Card: bg-card surface, subtle border + shadow.
        "rounded-lg border bg-card p-3 shadow-sm",
        interactive && "transition-colors hover:bg-accent/60",
        active && "ring-2 ring-primary",
        dimmed && "opacity-55",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        {icon && <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>}
      </div>
      <div className={cn("mt-1 text-2xl font-semibold tabular-nums", TONE_CLASS[tone])}>{value}</div>
      {sub != null && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
